// @erp/ui/console-layout — the loading → unreachable → session-gate →
// permission-gate sequence that was hand-duplicated in all 8 admin console
// layout files (library-catalogue.tsx, library-patrons.tsx, ..., cms.tsx).
// Same gating behavior as before (the root layout still gates nothing — this
// is the per-screen gate DEV-GUIDE-002 §4 documents, just no longer
// copy-pasted). Renders inside AppShell's <main>, so it declares no landmark
// of its own — a route must never end up with two <main>s on the page.
import { Show, type JSX } from "solid-js";
import { Navigate } from "@tanstack/solid-router";
import { useSession } from "@erp/auth-kit/fe";
import type { Permission } from "@erp/auth-kit/rbac";
import { PageHeading } from "../page-heading/index.js";
import { PageSkeleton } from "../page-skeleton/index.js";

export interface ConsoleLayoutProps {
  /**
   * The grant this console requires. An ARRAY means "any of" — the CMS console
   * opens for a session holding any single content capability, so an editor
   * with only cms:events still gets in and the per-screen gates narrow from
   * there. Anonymous → /login; signed-in without the grant → the denied panel.
   */
  perm: Permission | readonly Permission[];
  deniedMessage: string;
  skeletonId: string;
  deniedId?: string;
  unreachableId?: string;
  children: JSX.Element;
}

export function ConsoleLayout(props: ConsoleLayoutProps) {
  const auth = useSession();
  const allowed = () =>
    Array.isArray(props.perm)
      ? (props.perm as readonly Permission[]).some((p) => auth.can(p))
      : auth.can(props.perm as Permission);

  return (
    <Show when={!auth.loading()} fallback={<PageSkeleton id={props.skeletonId} />}>
      {/* /me errored past its retries → server down, NOT signed out: show a
          reachability notice instead of bouncing a valid session to /login. */}
      <Show
        when={!auth.error()}
        fallback={
          <div id={props.unreachableId} role="alert">
            <PageHeading title="Can't reach the server" subtitle="Retrying — check your connection." />
          </div>
        }
      >
        <Show when={auth.session()} fallback={<Navigate to="/login" />}>
          <Show
            when={allowed()}
            fallback={
              <div
                id={props.deniedId}
                role="alert"
                class="rounded-500 border border-base-300 bg-base-100 p-6 text-sm text-base-subtle"
              >
                {props.deniedMessage}
              </div>
            }
          >
            {props.children}
          </Show>
        </Show>
      </Show>
    </Show>
  );
}
